import { Box, useDisclosure } from '@chakra-ui/react';
import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import MapFrame from '../components/Web/Map/MapFrame/MapFrame';
import WeatherInfo from '../components/Web/Map/WeatherInfo/';
import RouteProfile from '../components/Web/Map/RouteProfile';
import {
  WeatherWarningsModal,
  WeatherWarningsBanner,
} from '../components/Web/Map/WeatherWarnings';
import { useToast } from '@chakra-ui/react';
import LoadingMap from '../components/Misc/LoadingMap/LoadingMap';
import RouteApi from '../api/RouteApi';
import ReactGA from 'react-ga4';
import LayoutBasic from '../layouts/LayoutBasic';
import {
  analyzeRouteForWarnings,
  hasUrgentWarnings,
} from '../util/WeatherWarnings';
import {
  computePassingTimes,
  filterForecastFromDeparture,
} from '../util/passingTimes';
ReactGA.initialize('G-YMJYGJM7BD');

const CANONICAL_URL = 'https://www.routeforecast.app/map';
const DEFAULT_OG_IMAGE = 'https://www.routeforecast.app/ogimage.jpg';

export default function Map() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'es';
  const [absoluteLocalities, setAbsoluteLocalities] = useState([]);
  const [localitiesAndWeather, setLocalitiesAndWeather] = useState([]);
  const [selectedHour, setSelectedHour] = useState(0);
  const [indexToRender, setIndexToRender] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apiData, setApiData] = useState(null);
  const [departureTime, setDepartureTime] = useState(() => new Date());
  const { routeId } = useParams();
  const toast = useToast();

  // Route elevation profile + share drawer
  const [activeRouteId, setActiveRouteId] = useState(routeId || null);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileDark, setProfileDark] = useState(true);

  // Weather warnings modal
  const {
    isOpen: isWarningsOpen,
    onOpen: onWarningsOpen,
    onClose: onWarningsClose,
  } = useDisclosure();

  const {
    isOpen: isProfileOpen,
    onOpen: onProfileOpen,
    onClose: onProfileClose,
  } = useDisclosure();

  // Localities enriched with the estimated passing time and trimmed to the
  // hours relevant to the chosen departure. Recomputed whenever the weather
  // data or the departure time changes (no refetch needed).
  const displayLocalities = useMemo(() => {
    if (!localitiesAndWeather || localitiesAndWeather.length === 0) return [];
    // Use the finish point's timeToArrive from the pre-deduplication array as the
    // anchor so deduplication (which can drop the finish point) doesn't corrupt
    // the total duration used to distribute weather times.
    const lastAbsLocality = absoluteLocalities[absoluteLocalities.length - 1];
    const totalDurationSec =
      lastAbsLocality ? Number(lastAbsLocality.timeToArrive) || undefined : undefined;
    const withPassingTimes = computePassingTimes(
      localitiesAndWeather,
      departureTime,
      totalDurationSec
    );
    return withPassingTimes.map(locality => ({
      ...locality,
      weatherForecast: filterForecastFromDeparture(
        locality.weatherForecast,
        departureTime
      ),
    }));
  }, [localitiesAndWeather, departureTime, absoluteLocalities]);

  // Analyze weather data for warnings
  const warningsAnalysis = useMemo(() => {
    if (displayLocalities && displayLocalities.length > 0) {
      return analyzeRouteForWarnings(displayLocalities);
    }
    return null;
  }, [displayLocalities]);

  // Fetch the elevation profile whenever the active route id changes. The
  // profile is independent of the weather flow, so it loads in parallel.
  useEffect(() => {
    let active = true;
    const fetchProfile = async () => {
      setProfileLoading(true);
      try {
        const data = await RouteApi.getRouteProfile(activeRouteId);
        if (active) setProfile(data);
      } catch (e) {
        if (active) setProfile(null);
      } finally {
        if (active) setProfileLoading(false);
      }
    };
    if (activeRouteId) fetchProfile();
    return () => {
      active = false;
    };
  }, [activeRouteId]);

  const handleOpenProfile = isDark => {
    setProfileDark(!!isDark);
    onProfileOpen();
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (absoluteLocalities.length > 0) {
          // Deduplicate by localityName (works for both old and new route formats)
          const seenKeys = new Set();
          const uniqueLocalities = absoluteLocalities.filter(item => {
            const key = item.localityName || item.localityCode;
            if (!seenKeys.has(key)) {
              seenKeys.add(key);
              return true;
            }
            return false;
          });

          // Route old stored routes (with only localityCode) to AEMET endpoint;
          // new routes (with localityPoint) use Google Weather endpoint.
          const hasLocalityPoints = uniqueLocalities.some(l => l.localityPoint);
          let data;
          if (hasLocalityPoints) {
            const googleLocalities = uniqueLocalities.map(l => ({
              localityName: l.localityName,
              localityPoint: l.localityPoint,
              timeToArrive: l.timeToArrive,
            }));
            data = await RouteApi.getRouteWeatherGoogle(googleLocalities);
          } else {
            data = await RouteApi.getRouteWeather(uniqueLocalities);
          }

          if (data.definedLocalities) {
            // Keep the full hourly forecast; trimming to the relevant hours is
            // done reactively (see displayLocalities) so it can react to
            // departure-time changes without refetching.
            setLocalitiesAndWeather(data.definedLocalities);

            if (data.message) {
              toast({
                title: t('map.errors.weatherLoadTitle'),
                description: t('map.errors.weatherLoadDesc', { count: data.diff }),
                status: 'warning',
                duration: 4000,
                isClosable: true,
              });
            }

            setIsLoading(false);
          }
          // Do something with the weatherData, for example, update state or dispatch an action
        }
      } catch (error) {
        console.error('Error fetching weather data:', error);
        // Handle the error, you might want to show a toast or update state accordingly
      }
    };
    if (absoluteLocalities.length > 0) fetchData();
  }, [absoluteLocalities]);

  useEffect(() => {
    if (localitiesAndWeather && localitiesAndWeather.length > 0) {
      console.log(localitiesAndWeather);
      setIndexToRender(0);
    }
  }, [localitiesAndWeather, warningsAnalysis]);

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: '/map' });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const storedRoute = await RouteApi.getRouteById(routeId);
      console.log(storedRoute);
      if (storedRoute?.localities) {
        setApiData(storedRoute);
        setAbsoluteLocalities(storedRoute?.localities);
      }
    };
    if (routeId) {
      setIsLoading(true);
      fetchData();
    }
  }, [routeId]);

  // When viewing a stored route, point the OG/Twitter image at the dynamically
  // generated route card so shared links unfurl with a rich preview.
  const ogImage = activeRouteId
    ? RouteApi.getRouteOgImageUrl(activeRouteId)
    : DEFAULT_OG_IMAGE;

  return (
    <LayoutBasic>
      <Helmet>
        <title>{t('seo.mapTitle')}</title>
        <meta name="description" content={t('seo.mapDesc')} />
        <link rel="canonical" href={CANONICAL_URL} />
        <link rel="alternate" hrefLang="es" href={`${CANONICAL_URL}?lang=es`} />
        <link rel="alternate" hrefLang="en" href={`${CANONICAL_URL}?lang=en`} />
        <link rel="alternate" hrefLang="x-default" href={CANONICAL_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL_URL} />
        <meta property="og:title" content={t('seo.mapTitle')} />
        <meta property="og:description" content={t('seo.mapDesc')} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:locale" content={lang === 'en' ? 'en_US' : 'es_ES'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('seo.mapTitle')} />
        <meta name="twitter:description" content={t('seo.mapDesc')} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      <Box position="relative">
        {/* Weather Warnings Banner */}
        {warningsAnalysis && warningsAnalysis.hasWarnings && (
          <WeatherWarningsBanner
            warningsAnalysis={warningsAnalysis}
            onViewDetails={onWarningsOpen}
          />
        )}

        {/* Weather Warnings Modal */}
        <WeatherWarningsModal
          isOpen={isWarningsOpen}
          onClose={onWarningsClose}
          warningsAnalysis={warningsAnalysis}
          origin={localitiesAndWeather?.[0]?.localityName}
          destination={
            localitiesAndWeather?.[localitiesAndWeather.length - 1]
              ?.localityName
          }
        />

        {isLoading && <LoadingMap />}
        {indexToRender !== null && displayLocalities[indexToRender] && (
          <WeatherInfo
            index={indexToRender}
            maxIndex={displayLocalities.length - 1}
            data={displayLocalities[indexToRender]}
            setIndexToRender={setIndexToRender}
            selectedHour={selectedHour}
            setSelectedHour={setSelectedHour}
            warningsAnalysis={warningsAnalysis}
            hasWarningBanner={warningsAnalysis?.hasWarnings}
          />
        )}
        <Box
          h="100dvh"
          pt={warningsAnalysis?.hasWarnings ? '40px' : 0}
        >
          <MapFrame
            setIsLoading={setIsLoading}
            setLocalitiesAndWeather={setLocalitiesAndWeather}
            setAbsoluteLocalities={setAbsoluteLocalities}
            localitiesAndWeather={displayLocalities}
            setIndexToRender={setIndexToRender}
            indexToRender={indexToRender}
            routeData={apiData}
            hasWarningBanner={warningsAnalysis?.hasWarnings}
            departureTime={departureTime}
            setDepartureTime={setDepartureTime}
            onOpenProfile={handleOpenProfile}
            onRouteIdChange={setActiveRouteId}
          />
        </Box>

        <RouteProfile
          isOpen={isProfileOpen}
          onClose={onProfileClose}
          profile={profile}
          isLoading={profileLoading}
          localities={displayLocalities}
          origin={profile?.origin || displayLocalities?.[0]?.localityName}
          destination={
            profile?.destination ||
            displayLocalities?.[displayLocalities.length - 1]?.localityName
          }
          dark={profileDark}
          departureTime={departureTime}
          travelMode={profile?.travelMode}
        />
      </Box>
    </LayoutBasic>
  );
}
